import { BaseAction } from "../../../core/actions/BaseAction";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IFmlmView } from "../../../Orchestrator/Types/Interface/IFmlmView";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getLocaleFromCookie } from "../../../Lib/Utils/getLocaleFromCookie";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { DEFAULT_TIMEZONE } from "../../../config/global";
import { FmlmViewOffersResponseMapType } from "../../../Flows/FmlmDetail/Context/FmlmViewContext";
import { IGetFmlmViewInformationActionRequestParams, IGetFmlmViewInformationActionResponseParams } from "../../../Orchestrator/Types/Interface/ActionParams/GetFmlmViewInformationActionParams";
import { getFmlmJourneyWithOffersAction, VALID_FMLM_STOPS } from "./GetFmlmJourneyWithOffersAction";
import { IGetFmlmJourneyApiPayload } from "../../../Orchestrator/Types/Interface/ActionParams/GetFmlmJourneyWithOffersActionParams";
import { showNotification } from "../../../Services/notificationService";
import { FMLM_FILTER_TRANSPORT_MODES } from "../../../Components/TrainOffer/FmlmTrainOffer/FmlmFilterDrawer";
import { SearchStateType } from "../../../Orchestrator/Types/Index";

dayjs.extend(utc);
dayjs.extend(timezone);

const EMPTY_FMLM_VIEW_INFORMATION = {
  fmlmView: [] as IFmlmView[],
  fmlmViewOffersMap: new Map()
}

export class GetFmlmViewInformationAction extends BaseAction<IGetFmlmViewInformationActionResponseParams, IGetFmlmViewInformationActionRequestParams> {
  override async run(): Promise<IGetFmlmViewInformationActionResponseParams> {
    let fmlmViewOffersMapResult: FmlmViewOffersResponseMapType = new Map();
    let fmlmViewOffersResult = [] as IFmlmView[];

    if (!this.params) {
      return EMPTY_FMLM_VIEW_INFORMATION;
    }

    const { trainType, search, fmlmFilter, isTimerVisible, lastOfferDate, fmlmViewList, isFromPopUp } = this.params;

    updateRecoilState("trainTypeAtom", trainType)

    try {
      let journeyDate = trainType === "inbound" ? search?.inboundDate : search?.outboundDate;

      const searchState: SearchStateType = {
        ...search,
        fromName: trainType === "outbound" ? search.fromName : search.toName,
        toName: trainType === "outbound" ? search.toName : search.fromName,
        outboundDate: search.outboundDate,
        inboundDate: search.inboundDate,
        from: trainType === "outbound" ? search.from : search.to,
        to: trainType === "outbound" ? search.to : search.from,
        toGeoPosition: trainType === "outbound" ? search.toGeoPosition : search.fromGeoPosition,
        fromGeoPosition: trainType === "outbound" ? search.fromGeoPosition : search.toGeoPosition,
      };

      // Get journeyDate as a timestamp in ms in Dubai time zone
      let searchTime = Date.now();
      if (journeyDate) {
        try {
          if (fmlmFilter.mode === "leave") {
            searchTime = dayjs(journeyDate.toDate().toString())
              .tz(DEFAULT_TIMEZONE, true)
              .set("hour", fmlmFilter.hour)
              .set('minute', fmlmFilter.minute)
              .set("second", 0)
              .set("millisecond", 0)
              // .subtract(fmlmFilter.flexHr, 'hour')
              .valueOf()
          } else {
            searchTime = dayjs(journeyDate.toDate().toString())
              .tz(DEFAULT_TIMEZONE, true)
              .set("hour", fmlmFilter.hour)
              .set('minute', fmlmFilter.minute)
              .set("second", 0)
              .set("millisecond", 0)
              // .add(fmlmFilter.flexHr, 'hour')
              .valueOf();
          }
        } catch (e) {
          try {
            const dateObj = new Date(dayjs(journeyDate).toISOString())
            searchTime = dateObj.getTime();
          } catch {
            searchTime = Date.now();
          }
        }
      }

      if (lastOfferDate) {
        searchTime = lastOfferDate;
        updateRecoilState("lastFmlmFetchOfferDateAtom", lastOfferDate);
      } else {
        updateRecoilState("lastFmlmFetchOfferDateAtom", null);
      }

      const lang = await getLocaleFromCookie();

      if (!search?.fromGeoPosition?.latitude || !search?.fromGeoPosition?.longitude ||
        !search?.toGeoPosition?.latitude || !search?.toGeoPosition?.longitude) {

        showNotification("error", "Invalid Location", "Please select a valid location for FMLM search.");
        return EMPTY_FMLM_VIEW_INFORMATION;
      }

      let requestPayload: IGetFmlmJourneyApiPayload = {
        ...(fmlmFilter.mode === "leave" ? { departureTime: searchTime } : { arrivalTime: searchTime }),
        fromLat: searchState.fromGeoPosition?.latitude || 0,
        fromLon: searchState?.fromGeoPosition?.longitude || 0,
        fromName: searchState?.fromName || "",
        language: lang,
        // limit: 10, // commenting this out as it is not used in the current implementation
        toLat: searchState?.toGeoPosition?.latitude || 0,
        toLon: searchState?.toGeoPosition?.longitude || 0,
        toName: searchState?.toName || "",
        reverseOrder: fmlmFilter.mode === "arrive" ? "true": "false"
      };

      if (!isFromPopUp) {
        let originCity: string = ""
        let destinationCity: string = ""

        let bookableFmlmList = fmlmViewList?.map((view) => view.legs.filter(leg => leg.transitMode === "TRAIN")).filter(legs => legs.length > 0);

        if (bookableFmlmList && bookableFmlmList?.length > 0 && !isFromPopUp) {
          let firstFmlmOffer = bookableFmlmList[0];
          originCity = firstFmlmOffer[0].departurePoi.name;
          destinationCity = firstFmlmOffer[firstFmlmOffer.length - 1].arrivalPoi.name;
        }

        if (fmlmFilter.originTransportMode) {
          if (originCity) {
            let isValidExcludeOrigin = VALID_FMLM_STOPS.find(stop => stop === originCity);
            if (isValidExcludeOrigin) {
              let values = FMLM_FILTER_TRANSPORT_MODES.filter(mode => !fmlmFilter.originTransportMode.includes(mode.value) && !mode.disabled && mode?.exclusionType === "city").map(mode => mode?.excludeCode).filter(i => i);
              if (values.length) {
                requestPayload.exclusions = {
                  ...requestPayload.exclusions,
                  [originCity.toUpperCase()]: values
                }
              }
            }
          }

          let stationModes = FMLM_FILTER_TRANSPORT_MODES.filter(mode => !fmlmFilter.originTransportMode.includes(mode.value) && !mode.disabled).filter(mode => mode?.exclusionType === "origin").map(mode => mode?.excludeCode).filter(i => i);
          if (stationModes.length > 0) {
            requestPayload.exclusions = {
              ...requestPayload.exclusions,
              "DEPARTURE": stationModes
            }
          }
        }

        if (fmlmFilter.destinationTransportMode) {
          if (destinationCity) {
            let isValidExcludeDestination = VALID_FMLM_STOPS.find(stop => stop === destinationCity);
            if (isValidExcludeDestination) {
              let values = FMLM_FILTER_TRANSPORT_MODES.filter(mode => !fmlmFilter.destinationTransportMode.includes(mode.value) && !mode.disabled && mode?.exclusionType === "city").map(mode => mode?.excludeCode).filter(i => i);
              if (values.length) {
                requestPayload.exclusions = {
                  ...requestPayload.exclusions,
                  [destinationCity.toUpperCase()]: values
                }
              }
            }
          }

          let stationModes = FMLM_FILTER_TRANSPORT_MODES.filter(mode => !fmlmFilter.destinationTransportMode.includes(mode.value) && !mode.disabled).filter(mode => mode?.exclusionType === "origin").map(mode => mode?.excludeCode).filter(i => i);
          if (stationModes.length > 0) {
            requestPayload.exclusions = {
              ...requestPayload.exclusions,
              "ARRIVAL": stationModes
            }
          }
        }
      }

      if (fmlmFilter.sortBy) {
        if (fmlmFilter.sortBy !== "default") {
          requestPayload.orderBy = fmlmFilter.sortBy
        }
      }


      getFmlmJourneyWithOffersAction.setParams({
        payload: requestPayload,
        search: search,
        trainType: trainType
      })
      await getFmlmJourneyWithOffersAction.execute()

      if (getFmlmJourneyWithOffersAction.status.executionStatus === ExecutionStatus.SUCCESS) {
        let fmlmOffersList = getFmlmJourneyWithOffersAction.result?.fmlmView || [];
        let uniqueListFmlmOfferList = fmlmOffersList.filter((offer, index, self) =>
          index === self.findIndex(obj =>
            obj?.attributes?.STABLE_JOURNEY_SEGMENT_ID === offer?.attributes?.STABLE_JOURNEY_SEGMENT_ID
          )
        );

        if (this.params.lastOfferDate && this?.params?.fmlmViewList?.length && this?.params?.fmlmViewOffersMap?.size) {
          fmlmViewOffersResult = [...this?.params?.fmlmViewList, ...uniqueListFmlmOfferList || []];
          fmlmViewOffersMapResult = new Map([
            ...(this?.params?.fmlmViewOffersMap ? Array.from(this.params.fmlmViewOffersMap) : []),
            ...(getFmlmJourneyWithOffersAction.result?.fmlmViewOffersMap ? Array.from(getFmlmJourneyWithOffersAction.result.fmlmViewOffersMap) : [])
          ]);
        } else {
          fmlmViewOffersResult = uniqueListFmlmOfferList || [];
          fmlmViewOffersMapResult = new Map(getFmlmJourneyWithOffersAction.result?.fmlmViewOffersMap);


          if (trainType === 'outbound') {
            updateRecoilState<IFmlmView | null>("selectedFmlmJourneyAtom", null);
            updateRecoilState("selectedOutboundTrainAtom", null);
            updateRecoilState<IFmlmView | null>("selectedInboundFmlmJourneyAtom", null);
            updateRecoilState("selectedInboundTrainAtom", null);
            if (!lastOfferDate) {
              updateRecoilState("selectedTrainLegsAtom", {
                outbound: [],
                inbound: []
              });
            }
          } else if (trainType === "inbound") {
            updateRecoilState<IFmlmView | null>("selectedInboundFmlmJourneyAtom", null);
            updateRecoilState("selectedInboundTrainAtom", null);
          }
        }

        updateRecoilState<IFmlmView[]>("fmlmViewInformationAtom", fmlmViewOffersResult || []);
        updateRecoilState<FmlmViewOffersResponseMapType>("fmlmViewOffersMapAtom", fmlmViewOffersMapResult);
        updateRecoilState("isTimerVisibleAtom", isTimerVisible);
        updateRecoilState("isFmlmFilterTimerModelOpenAtom", isTimerVisible);
        updateRecoilState("isFmlmInboundTimeModalOpenAtom", isTimerVisible);
      }

      return {
        fmlmView: fmlmViewOffersResult,
        fmlmViewOffersMap: fmlmViewOffersMapResult
      };
    } catch (error) {
      console.error("Error in OpenSubStationDropdownAction:", error);
      return EMPTY_FMLM_VIEW_INFORMATION;
    } finally {
    }
  }
}

export const getFmlmViewInformationAction = new GetFmlmViewInformationAction();
